import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import _ from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import dayjs from 'dayjs';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any[];
    timeSeriesObjKey: string;
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
    };
    url: string;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchFeatureTimeSeries({
    queryKey,
    timeSeriesObjKey,
    variables = {},
    queryConfig = {},
    url,
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const { appConfig } = useContext(UserContext);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        // if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            // if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData: any = {};
            let minVal = Infinity;
            let maxVal = -Infinity;
            const newData = payload?.map((item: any) => {
                minVal = Math.min(minVal, item.data);
                maxVal = Math.max(maxVal, item.data);

                return {
                    period: dayjs(item.startTimestamp).format('MM/YYYY'),
                    [timeSeriesObjKey]: item.data,
                };
            });
            parsedData.data = newData;
            parsedData.minVal = minVal;
            parsedData.maxVal = maxVal;

            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchFeatureTimeSeries };
